import React, { useEffect, useRef, useState } from 'react';

/**
 * ItemDetail — the one `DrillOver` body for `kind:'item'` (Epic 6 · S6.1, Part A.4).
 *
 * The detail view of a single piece of work, opened from any row / chip / avatar /
 * "Show all" / "Reply" on the page. The shell (`PortalPage`) fetches the drill
 * payload (`fn_portal_drill('item', id, {park_id})`) and hands the resolved row to
 * this presentational component — ItemDetail itself never imports the data layer
 * (Epic 1: "no Portal component imports supabase directly").
 *
 * Layout (Part E.2 / A.4): title · park (multi-park only) · stage chip ·
 * holder_label · when chip · asked_label / asked_by · timeline (the same four
 * stops, labelled) · messages (oldest first; `[]` -> "No messages about this
 * yet.") · a reply composer.
 *
 * Reply composer: hidden when `row.reply.can_write === false` (the team-member
 * read-only state, Part G — verified live: staff login returns
 * `reply.can_write:false`). `focusComposer` (carried on the frame by WaitingRow's
 * "Reply" action) focuses the textarea on mount. Enter sends, Shift+Enter is a
 * newline. `body` is rendered verbatim with line breaks, never as HTML.
 *
 * Colour (Part C): a single neutral stage chip + a neutral when chip; the only
 * accent on the timeline is the brand ring on the current (last) stop. No
 * red/amber anywhere — colour is never the only carrier.
 */
const NEUTRAL_CHIP = 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-300';
const BRAND = '#32B1FF';

const Chip = ({ children, ...rest }) => (
  <span
    className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium whitespace-nowrap ${NEUTRAL_CHIP}`}
    {...rest}
  >
    {children}
  </span>
);

// The labelled four-stop timeline. Each event is `{ key, label, when, who }`.
// The last event is the current stop — it carries the brand ring (the single
// accent on the row); the rest are reached stops in the brand fill.
const Timeline = ({ events }) => {
  const list = Array.isArray(events) ? events : [];
  return (
    <ol className="space-y-3" data-testid="item-timeline">
      {list.map((ev, i) => {
        const current = i === list.length - 1;
        return (
          <li key={ev?.key ?? i} className="flex gap-3">
            <span className="flex flex-col items-center">
              <span
                className={`mt-1 w-2.5 h-2.5 rounded-full shrink-0 ${
                  current
                    ? `ring-2 ring-offset-2 ring-[${BRAND}] dark:ring-offset-gray-900 bg-[${BRAND}]`
                    : `bg-[${BRAND}]`
                }`}
                data-state={current ? 'current' : 'done'}
              />
              {i < list.length - 1 && <span className="w-px flex-1 bg-gray-200 dark:bg-gray-700" />}
            </span>
            <div className="min-w-0 pb-1">
              <div className="gi-body text-sm font-medium text-gray-900 dark:text-gray-100 break-words">
                {ev?.label}
              </div>
              {(ev?.when || ev?.who) && (
                <div className="gi-label text-gray-500 dark:text-gray-400 mt-0.5 normal-case tracking-normal">
                  {[ev?.when, ev?.who].filter(Boolean).join(' · ')}
                </div>
              )}
            </div>
          </li>
        );
      })}
    </ol>
  );
};

// One message bubble. `mine` -> right + brand; otherwise left + neutral. `body`
// is rendered verbatim with line breaks preserved (whitespace-pre-wrap), never
// as HTML (linkify is Epic 7's concern — here we only show text).
const MessageBubble = ({ msg }) => {
  const mine = !!msg?.mine;
  return (
    <div className={`flex ${mine ? 'justify-end' : 'justify-start'}`}>
      <div
        className={`max-w-[85%] rounded-2xl px-3 py-2 ${
          mine
            ? 'bg-[#32B1FF] text-white rounded-br-sm'
            : 'bg-gray-100 dark:bg-gray-800 text-gray-900 dark:text-gray-100 rounded-bl-sm'
        }`}
        data-testid="item-message"
        data-mine={mine ? 'true' : undefined}
      >
        {!mine && msg?.from && (
          <div className="text-xs font-medium opacity-80 mb-0.5">{msg.from}</div>
        )}
        <div className="text-sm whitespace-pre-wrap break-words">{msg?.body}</div>
        {msg?.when && (
          <div className={`text-xs mt-1 ${mine ? 'text-white/70' : 'text-gray-500 dark:text-gray-400'}`}>
            {msg.when}
          </div>
        )}
      </div>
    </div>
  );
};

// Reply composer (hidden when `reply.can_write === false`). Enter sends,
// Shift+Enter is a newline. `focusComposer` focuses the textarea on mount.
const Composer = ({ focusComposer, disabled, onSend }) => {
  const ref = useRef(null);
  const [body, setBody] = useState('');
  const [sending, setSending] = useState(false);
  const [err, setErr] = useState(null);

  useEffect(() => {
    if (focusComposer) ref.current?.focus();
  }, [focusComposer]);

  const send = async () => {
    const text = body.trim();
    if (!text || sending || disabled) return;
    setSending(true);
    setErr(null);
    try {
      await onSend?.(text);
      setBody('');
    } catch (e) {
      setErr(e?.message || 'Couldn’t send.');
    } finally {
      setSending(false);
    }
  };

  const onKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  return (
    <div className="mt-4" data-testid="item-composer">
      <textarea
        ref={ref}
        value={body}
        disabled={disabled || sending}
        onChange={(e) => setBody(e.target.value)}
        onKeyDown={onKeyDown}
        rows={2}
        placeholder="Write a reply…"
        aria-label="Reply"
        className="w-full resize-y rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 px-3 py-2 text-sm text-gray-900 dark:text-gray-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#32B1FF]"
      />
      <div className="flex items-center justify-between gap-3 mt-2">
        {err ? (
          <p className="text-sm text-red-600 dark:text-red-400" role="alert">{err}</p>
        ) : (
          <p className="text-xs text-gray-400 dark:text-gray-500">Enter to send · Shift+Enter for a new line</p>
        )}
        <button
          type="button"
          onClick={send}
          disabled={disabled || sending || !body.trim()}
          className="inline-flex items-center justify-center min-h-[44px] px-4 rounded-lg text-sm font-medium text-white bg-[#32B1FF] hover:bg-[#1aa3f5] disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none focus-visible:ring-2 focus-visible:ring-[#32B1FF]"
        >
          {sending ? 'Sending…' : 'Send'}
        </button>
      </div>
    </div>
  );
};

const ItemDetail = ({ row, multiPark = false, focusComposer = false, onSend }) => {
  if (!row) {
    return <p className="py-4 text-sm text-gray-500 dark:text-gray-400">This item could not be loaded.</p>;
  }

  const messages = Array.isArray(row.messages) ? row.messages : [];
  const timeline = Array.isArray(row.timeline) ? row.timeline : [];
  const canWrite = row.reply?.can_write !== false;
  const parkId = row.reply?.p_client_id ?? row.park_id;
  const itemId = row.reply?.p_task_id ?? row.id;
  const handleSend = (body) => onSend?.({ parkId, itemId, body });

  return (
    <div className="space-y-4 py-2" data-testid="item-detail" data-item-id={row.id}>
      <div className="space-y-2">
        <h2 className="gi-body text-base font-semibold text-gray-900 dark:text-gray-100 break-words">
          {row.title}
        </h2>
        <div className="flex flex-wrap items-center gap-2">
          {multiPark && row.park && (
            <span className="text-xs text-gray-500 dark:text-gray-400 truncate">{row.park}</span>
          )}
          {row.stage_label && <Chip data-testid="item-stage-chip">{row.stage_label}</Chip>}
          {row.holder_label && (
            <span className="text-xs text-gray-500 dark:text-gray-400">With {row.holder_label}</span>
          )}
          {row.when && <Chip data-testid="item-when-chip">{row.when}</Chip>}
          {row.asked_label && (
            <span className="text-xs text-gray-500 dark:text-gray-400">{row.asked_label}</span>
          )}
          {row.asked_by && (
            <span className="text-xs text-gray-500 dark:text-gray-400">asked by {row.asked_by}</span>
          )}
        </div>
      </div>

      {timeline.length > 0 && (
        <div className="border-t border-gray-100 dark:border-gray-800 pt-3">
          <h3 className="gi-label text-gray-500 dark:text-gray-400 mb-2">Timeline</h3>
          <Timeline events={timeline} />
        </div>
      )}

      <div className="border-t border-gray-100 dark:border-gray-800 pt-3">
        <h3 className="gi-label text-gray-500 dark:text-gray-400 mb-2">Messages</h3>
        {messages.length === 0 ? (
          <p className="text-sm text-gray-500 dark:text-gray-400" data-testid="item-messages-empty">
            No messages about this yet.
          </p>
        ) : (
          <div className="space-y-2" data-testid="item-messages">
            {messages.map((m, i) => (
              <MessageBubble key={m?.id ?? i} msg={m} />
            ))}
          </div>
        )}
      </div>

      {canWrite && <Composer focusComposer={focusComposer} onSend={handleSend} />}
      {!canWrite && (
        <p className="text-xs text-gray-400 dark:text-gray-500 border-t border-gray-100 dark:border-gray-800 pt-3">
          You’re viewing this read-only.
        </p>
      )}
    </div>
  );
};

export default ItemDetail;

